import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@akuna/ui";
import { useItems } from "@/hooks/useItems";
import { useCreateStockMovement, type StockMovementInput } from "@/hooks/useStockMovements";
import { useTranslation } from "react-i18next";
import { Package } from "lucide-react";
import { cn } from "@/lib/utils";

const movementSchema = z.object({
  item_id: z.string().min(1, "Item é obrigatório"),
  type: z.enum([
    "ENTRADA",
    "SAIDA",
    "AJUSTE",
    "AJUSTE_POSITIVO",
    "AJUSTE_NEGATIVO",
    "CONSUMO_INTERNO",
    "DEVOLUCAO_CLIENTE",
    "DEVOLUCAO_FORNECEDOR",
  ]),
  quantity: z.coerce.number().positive("Quantidade deve ser positiva"),
  unit_cost: z.coerce.number().optional(),
  batch_number: z.string().optional(),
  reason: z.string().optional(),
});

type MovementFormValues = z.infer<typeof movementSchema>;

/** Map dialog movement type to product_stock_movements type and quantity */
function toStockMovementType(
  type: MovementFormValues["type"],
  quantity: number
): { type: "ENTRADA" | "SAIDA" | "AJUSTE"; quantity: number } {
  switch (type) {
    case "ENTRADA":
    case "DEVOLUCAO_CLIENTE":
    case "DEVOLUCAO_FORNECEDOR":
      return { type: "ENTRADA", quantity };
    case "SAIDA":
    case "CONSUMO_INTERNO":
    case "AJUSTE_NEGATIVO":
      return { type: "SAIDA", quantity };
    case "AJUSTE":
    case "AJUSTE_POSITIVO":
      return { type: "AJUSTE", quantity };
    default:
      return { type: "ENTRADA", quantity };
  }
}

interface StockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId?: string | null;
  itemName?: string | null;
  defaultType?: "ENTRADA" | "SAIDA";
}

export function StockMovementDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
  defaultType = "ENTRADA",
}: StockMovementDialogProps) {
  const { t } = useTranslation("erp");
  const isItemLocked = Boolean(itemId);
  const { data: itemsResult } = useItems({ type: "PRODUTO" });
  const items = Array.isArray(itemsResult) ? itemsResult : itemsResult?.data ?? [];
  const createMovement = useCreateStockMovement();

  const form = useForm<MovementFormValues>({
    resolver: zodResolver(movementSchema),
    defaultValues: {
      item_id: "",
      type: "ENTRADA",
      quantity: 0,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        item_id: itemId ?? "",
        type: defaultType,
        quantity: 0,
        reason: "",
      });
    } else {
      form.reset();
    }
  }, [defaultType, form, itemId, open]);

  const onSubmit = async (data: MovementFormValues) => {
    const { type, quantity } = toStockMovementType(data.type, data.quantity);
    const input: StockMovementInput = {
      item_id: data.item_id,
      type,
      quantity,
      reason: data.reason || undefined,
    };
    await createMovement.mutateAsync(input);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {itemName
              ? t("dialogs.stockMovement.titleWithItem", { item: itemName })
              : t("dialogs.stockMovement.title")}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {isItemLocked ? (
              <div className="flex items-center gap-3 rounded-lg border bg-muted/25 px-3 py-2">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-600">
                  <Package className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t("dialogs.stockMovement.item")}</p>
                  <p className="truncate text-sm font-medium">{itemName}</p>
                </div>
              </div>
            ) : (
              <FormField
                control={form.control}
                name="item_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("dialogs.stockMovement.item")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? ""}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("dialogs.stockMovement.itemPlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {items?.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name} {item.barcode && `(${item.barcode})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("dialogs.stockMovement.movementType")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ENTRADA">{t("dialogs.stockMovement.types.ENTRADA")}</SelectItem>
                      <SelectItem value="SAIDA">{t("dialogs.stockMovement.types.SAIDA")}</SelectItem>
                      {!isItemLocked && (
                        <>
                          <SelectItem value="AJUSTE">{t("dialogs.stockMovement.types.AJUSTE")}</SelectItem>
                          <SelectItem value="AJUSTE_POSITIVO">{t("dialogs.stockMovement.types.AJUSTE_POSITIVO")}</SelectItem>
                          <SelectItem value="AJUSTE_NEGATIVO">{t("dialogs.stockMovement.types.AJUSTE_NEGATIVO")}</SelectItem>
                          <SelectItem value="CONSUMO_INTERNO">{t("dialogs.stockMovement.types.CONSUMO_INTERNO")}</SelectItem>
                          <SelectItem value="DEVOLUCAO_CLIENTE">{t("dialogs.stockMovement.types.DEVOLUCAO_CLIENTE")}</SelectItem>
                          <SelectItem value="DEVOLUCAO_FORNECEDOR">{t("dialogs.stockMovement.types.DEVOLUCAO_FORNECEDOR")}</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className={cn("grid gap-4", isItemLocked ? "grid-cols-1" : "grid-cols-2")}>
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("dialogs.stockMovement.quantity")}</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isItemLocked && (
                <FormField
                  control={form.control}
                  name="unit_cost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("dialogs.stockMovement.unitCost")}</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" placeholder="0.00" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {!isItemLocked && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="batch_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("dialogs.stockMovement.batchNumber")}</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: L123456" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("dialogs.stockMovement.reason")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("dialogs.stockMovement.reasonPlaceholder")}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={createMovement.isPending}>
                {createMovement.isPending ? t("dialogs.stockMovement.saving") : t("dialogs.stockMovement.create")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
